// Hook autenticazione — wraps Zustand store + operazioni login/logout/refresh
import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import type { AuthTokens } from '@fieldcore/types'
import { useAuthStore } from '../stores/auth.store'
import api from '../lib/api'
import { destroySocket } from '../lib/socket'
import { queryClient } from '../lib/queryClient'

interface LoginPayload {
  email: string
  password: string
  totpCode?: string
}

interface LoginResponse {
  accessToken: string
  refreshToken: string
  expiresIn: number
  user: {
    id: string
    email: string
    firstName: string
    lastName: string
    role: string
    twoFaEnabled: boolean
    isActive: boolean
    createdAt: string
    updatedAt: string
  }
  requiresTwoFactor?: boolean
}

export function useAuth() {
  const navigate = useNavigate()
  const store = useAuthStore()

  const loginMutation = useMutation({
    mutationFn: async (payload: LoginPayload): Promise<LoginResponse> => {
      const response = await api.post<LoginResponse>('/auth/login', payload)
      return response.data
    },
    onSuccess: (data) => {
      if (data.requiresTwoFactor) {
        store.setRequiresTwoFactor(data.user?.email ?? '')
        navigate('/2fa')
      } else {
        const tokens: AuthTokens = {
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          expiresIn: data.expiresIn ?? 3600,
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        store.setAuth(tokens, data.user as any)
        navigate('/')
      }
    },
  })

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout')
    } catch {
      // Ignora errori di logout
    } finally {
      destroySocket()
      store.clearAuth()
      queryClient.clear()
      navigate('/login')
    }
  }, [navigate, store])

  const isAuthenticated = store.isAuthenticated
  const user = store.user
  const requiresTwoFactor = store.requiresTwoFactor

  const hasRole = useCallback(
    (roles: string | string[]) => {
      if (!user) return false
      const roleArray = Array.isArray(roles) ? roles : [roles]
      return roleArray.includes(user.role)
    },
    [user]
  )

  return {
    user,
    isAuthenticated,
    requiresTwoFactor,
    hasRole,
    login: loginMutation.mutate,
    loginAsync: loginMutation.mutateAsync,
    isLoginLoading: loginMutation.isPending,
    loginError: loginMutation.error,
    logout,
  }
}
